import { useState } from 'react';
import { Mail } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

interface AuthFormProps {
  onAuthSuccess: () => void;
}

const AuthForm = ({ onAuthSuccess }: AuthFormProps) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      toast.success('Password reset link sent! Check your email.');
      setIsForgotPassword(false);
    } catch (error: any) {
      console.error('Reset password error:', error);
      toast.error(error.message);
    }
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isSignUp) {
        console.log('Attempting to sign up...');
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
        });
        
        if (error) throw error;
        
        if (data.user && !data.user.email_confirmed_at) {
          setShowVerificationDialog(true);
          return;
        }
        
        toast.success('Account created successfully!');
      } else {
        console.log('Attempting to sign in...');
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        
        if (error) throw error;
        
        if (data.user && !data.user.email_confirmed_at) {
          toast.error('Please verify your email before signing in');
          return;
        }
      }
      onAuthSuccess();
    } catch (error: any) {
      console.error('Auth error:', error);
      toast.error(error.message);
    }
  };

  return (
    <div className="max-w-md mx-auto space-y-8">
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold text-gray-900">
          Sales Call Analyzer
        </h1>
        <p className="text-lg text-gray-600">
          {isSignUp ? 'Create an account' : 'Sign in'} to analyze your sales calls
        </p>
      </div>
      
      <Alert>
        <AlertDescription>
          Please {isSignUp ? 'sign up' : 'sign in'} to upload and analyze your sales calls
        </AlertDescription>
      </Alert>

      {isForgotPassword ? (
        <form onSubmit={handleForgotPassword} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="email" className="text-sm font-medium">Email</label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
            />
          </div>
          <Button type="submit" className="w-full">
            <Mail className="w-4 h-4 mr-2" />
            Send Reset Link
          </Button>
          <div className="text-center">
            <button
              onClick={() => setIsForgotPassword(false)}
              className="text-primary hover:underline text-sm"
            >
              Back to sign in
            </button>
          </div>
        </form>
      ) : (
        <>
          <form onSubmit={handleAuth} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium">Email</label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium">Password</label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
              />
            </div>
            <Button type="submit" className="w-full">
              <Mail className="w-4 h-4 mr-2" />
              {isSignUp ? 'Sign Up' : 'Sign In'}
            </Button>
          </form>

          {!isSignUp && (
            <div className="text-center">
              <button
                onClick={() => setIsForgotPassword(true)}
                className="text-primary hover:underline text-sm"
              >
                Forgot password?
              </button>
            </div>
          )}

          <div className="text-center">
            <button
              onClick={() => setIsSignUp(!isSignUp)}
              className="text-primary hover:underline text-sm"
            >
              {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
            </button>
          </div>
        </>
      )}

      <Dialog open={showVerificationDialog} onOpenChange={setShowVerificationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Verify your email</DialogTitle>
            <DialogDescription className="pt-4">
              We've sent you a verification email to <strong>{email}</strong>. Please check your inbox and click the verification link to activate your account.
              <br /><br />
              Once verified, you can sign in to start analyzing your sales calls.
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuthForm;